import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useParams } from 'react-router-dom';
import { Button, ButtonGroup, color, spacing, Typography } from '@uniquegood/realworld-studio-design';

import { onMobile } from '@/styles/responsive';
import { CardList, MissionViewButton } from '@/pages/missions/components/missionList';
import { MissionEditMode, MissionViewType } from '@/pages/missions/types';
import { useIsMobile } from '@/hooks';
import useRequest from '@/hooks/useRequest';
import useMission from '@/hooks/useMission';
import { FlexGrow } from '@/styles';
import { TOP_BAR_HEIGHT } from '@/components/TopBar/Topbar';

interface MissionListBarProps {
  missionEditMode: MissionEditMode;
  onChangeMissionEditMode: (mode: MissionEditMode) => void;
}

function MissionListBar({ missionEditMode, onChangeMissionEditMode }: MissionListBarProps) {
  const { appId, projectId } = useParams<AppParam>();
  const isMobile = useIsMobile();
  const { data: app } = useRequest<RealWorldApp>(appId ? `/apps/${appId}` : null);
  const isRealworldChannel = app?.channelType === 'Realworld';

  const rootRef = useRef<HTMLDivElement>(null);
  const [missionView, setMissionView] = useState<MissionViewType>('CardView');

  const { missions } = useMission();

  useEffect(() => {
    if (isMobile) {
      setMissionView('CardView');
    }
  }, [isMobile]);

  return (
    <nav css={[MissionCardListStyle, FlexGrow]} ref={rootRef}>
      <header css={[MissionCardHeaderStyle, isMobile ? MobileStyle : DesktopStyle]}>
        {isRealworldChannel && (
          <ButtonGroup segmented cssStyle={MissionEditModeTabsStyle}>
            <Button
              size="small"
              type={missionEditMode === 'missionCard' ? 'primary' : 'outlinePrimary'}
              pressed={missionEditMode === 'missionCard'}
              onClick={() => onChangeMissionEditMode('missionCard')}
            >
              미션 카드 방식
            </Button>
            <Button
              size="small"
              type={missionEditMode === 'flow' ? 'primary' : 'outlinePrimary'}
              pressed={missionEditMode === 'flow'}
              onClick={() => onChangeMissionEditMode('flow')}
              disabled={!projectId}
            >
              플로우 방식
            </Button>
          </ButtonGroup>
        )}

        <div css={MissionCardMetaHeaderStyle}>
          <Typography as="h4" type="button">
            미션 {missions?.length ?? 0}개
          </Typography>
          <MissionViewButton missionView={missionView} onChangeMissionView={setMissionView} />
        </div>
      </header>

      <div css={ListLayout}>
        <CardList root={rootRef.current} missionView={missionView} />
      </div>
    </nav>
  );
}

export default MissionListBar;

const MissionCardHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.small};
`;

const MissionCardMetaHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MissionEditModeTabsStyle = css`
  width: 100%;

  button {
    flex: 1;
  }
`;

const ListLayout = css`
  padding: ${spacing.common.large} ${spacing.common.large};

  ${onMobile} {
    display: flex;
    padding: 0px;
  }
`;

export const MissionListBarMobileHeight = '252px';

const MissionCardListStyle = css`
  position: fixed;
  height: calc(100vh - ${TOP_BAR_HEIGHT});
  top: 58px;

  background-color: ${color.background_default};
  && {
    flex: 0 0 240px;
  }

  overflow-y: auto;

  ${onMobile} {
    position: initial;
    overflow-x: auto;
    overflow-y: initial;
    height: initial;

    padding: ${spacing.common.medium};
    width: 100%;
  }
`;

const DesktopStyle = css`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${color.background_default};
  padding: ${spacing.common.large};
  padding-bottom: ${spacing.common.small};
  z-index: 1;
`;

const MobileStyle = css`
  margin-bottom: ${spacing.common.medium};
`;
